import React, { forwardRef } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { Box, Typography } from '@mui/material';
import PublisherCard from '../../../components/publisherCard';
import PublisherCardSkeleton from '../../../components/publisherCardSkeleton';
import LoadingAnimation from '../../../components/loadingAnimation';
import * as styles from './styles';

const GridList = forwardRef(({ style, children, ...props }, ref) => (
    <Box ref={ref} sx={styles.virtualizedList} style={style} {...props}>
        {children}
    </Box>
));
GridList.displayName = 'GridList';

const GridItem = forwardRef(({ children, ...props }, ref) => (
    <Box ref={ref} sx={styles.virtualizedItem} {...props}>
        {children}
    </Box>
));
GridItem.displayName = 'GridItem';

function DirectoryList({ publishers, whatsHotPublisherIds, loading, loadingMore, hasMore, onSync, onShare, onLoadMore }) {
    function renderEmpty() {
        return (
            <Box sx={styles.empty}>
                <Typography>No publishers match those filters. Try clearing some.</Typography>
            </Box>
        );
    }

    function renderSkeletonCards() {
        return Array.from({ length: styles.MAX_GRID_COLUMNS }, (_, i) => <PublisherCardSkeleton key={i} />);
    }

    function renderEndOfList() {
        return (
            <Box sx={styles.endOfList}>
                <Box sx={styles.endOfListLine} />
                {/* Copy TBD with Pat — placeholder wording for now (EBL-13079) */}
                <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
                    You&apos;ve reached the end of the list.
                </Typography>
                <Box sx={styles.endOfListLine} />
            </Box>
        );
    }

    function renderFooter() {
        if (loadingMore) {
            return (
                <Box sx={styles.footer}>
                    <LoadingAnimation />
                </Box>
            );
        }
        if (!hasMore) return renderEndOfList();
        return null;
    }

    if (loading) {
        return <Box sx={styles.grid}>{renderSkeletonCards()}</Box>;
    }

    if (publishers.length === 0) {
        return <Box sx={styles.grid}>{renderEmpty()}</Box>;
    }

    // The loading/end-of-list indicator is a plain sibling below the grid,
    // not a VirtuosoGrid `components.Footer` — keeping it outside Virtuoso's
    // own measured/virtualized DOM means hovering or scrolling over it can't
    // feed back into its scroll-position math.
    return (
        <>
            <VirtuosoGrid
                useWindowScroll
                data={publishers}
                computeItemKey={(index, publisher) => publisher?.pubId || index}
                components={{ List: GridList, Item: GridItem }}
                endReached={hasMore ? onLoadMore : undefined}
                itemContent={(index, publisher) => (
                    <PublisherCard
                        publisher={publisher}
                        hotRank={whatsHotPublisherIds[String(publisher.pubId)]}
                        onSync={onSync}
                        onShare={onShare}
                    />
                )}
            />
            {renderFooter()}
        </>
    );
}

export default DirectoryList;
